/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { IStackProps, IStackSlots, Stack } from '@fluentui/react/lib/Stack';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { EmptyStateView } from 'src/core/components/EmptyStateView';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { resultCardStyles, resultCardTitleStyles } from 'src/languageStudio/appShell/prebuiltDemos/utils/commonStyles';
import { ExtractedSummarySentence } from 'src/languageStudio/core/services/textAnalytics/textAnalyticsSwaggers';

export type SummarizationCardProps = {
    sentences: ExtractedSummarySentence[];
    isLoading: boolean;
    isOnlyCard?: boolean;
    isExtractive?: boolean;
};

const messages = defineMessages({
    summary: {
        id: 'languageStudio.summarizationCard.summary',
        defaultMessage: `Summary`
    },
    emptyCardMessage: {
        id: 'languageStudio.summarizationCard.emptyCardMessage',
        defaultMessage: 'There are no sentences'
    }
});

const theme = getScopedTheme('DemoPage');

const summarizationCardStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({
    root: { maxWidth: '1000px', minHeight: '150px' }
});

const summarizationTwoCardsStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({
    root: { maxWidth: '499px', minHeight: '196px' }
});

export const SummarizationCard = (props: SummarizationCardProps) => {
    const { sentences, isLoading, isOnlyCard = true, isExtractive = true } = props;
    const { localize } = useLocalization();

    return isLoading ? (
        <Spinner size={SpinnerSize.large} />
    ) : (
        <Stack
            tokens={{ childrenGap: 7 }}
            styles={mergeStyleSets(resultCardStyles, isOnlyCard ? summarizationCardStyles : summarizationTwoCardsStyles)}>
            <LuisText
                fontWeight={500}
                styles={resultCardTitleStyles(isExtractive ? theme.extractiveCardColor : theme.abstractiveCardColor)}>
                {localize(messages.summary)}
            </LuisText>

            <ul>
                <Stack tokens={{ childrenGap: 5, padding: '0 0 0 20px' }}>
                    {sentences.length > 0 ? (
                        sentences?.map((sentence, sentenceIndex) => (
                            <li key={sentenceIndex}>
                                <LuisText fontWeight={500}>{sentence.text}</LuisText>
                            </li>
                        ))
                    ) : (
                        <EmptyStateView imageStyles={{ image: { height: 80 } }} message={localize(messages.emptyCardMessage)} />
                    )}
                </Stack>
            </ul>
        </Stack>
    );
};
